<template>
  <div class="screen-wrapper">
    <ChatHeader
      :assistant="ReviewingAssistantType.DocumentReviewingAssistant"
      :reset-conversation="() => onResetReview(false)"
      :show-history="false"
      :enable-new-conversation-button="
        String(activeTabIndex) === SubModulesEnum.DOC_REVIEWING_CHAT
      "
    />
    <CustomTabs v-model="activeTabIndex" tab-class="tab-grow">
      <CustomTab
        :id="SubModulesEnum.DOC_REVIEWING_CHAT"
        :title="$t('reviewing.chat.tab_title')"
        :disabled="
          !authStore.products.modules[ModulesEnum.DOC_REVIEWING].includes(
            SubModulesEnum.DOC_REVIEWING_CHAT
          )
        "
      >
        <ChatComponent
          ref="chatRef"
          v-bind="reviewingChatProps"
          :use-streaming-response="false"
        />
      </CustomTab>
      <CustomTab
        :id="SubModulesEnum.DOC_REVIEWING_KEYPOINTS"
        :title="$t('reviewing.key_points.tab_title')"
        :disabled="
          !authStore.products.modules[ModulesEnum.DOC_REVIEWING].includes(
            SubModulesEnum.DOC_REVIEWING_KEYPOINTS
          )
        "
      >
        <KeyPoints />
      </CustomTab>
    </CustomTabs>
  </div>
</template>

<script setup lang="ts">
import ChatHeader from '@/components/common/chat-header/ChatHeader.vue';
import ChatComponent, {
  ChatComponentProps
} from '@/components/common/chat/ChatComponent.vue';
import CustomTabs from '@/components/common/tabs/CustomTabs.vue';
import CustomTab from '@/components/common/tabs/tab/CustomTab.vue';
import KeyPoints from '@/components/Reviewing/key-points/KeyPoints.vue';
import ReferenceDots from '@/components/Reviewing/reference-dots/ReferenceDots.vue';
import { ModulesEnum, SubModulesEnum } from '@/core/modulesEnum';
import { ReviewingAssistantType } from '@/models/assistant-types';
import { ChatExposedMethods } from '@/models/chat.models';
import { Conversation } from '@/models/conversation';
import { ReviewMessage } from '@/models/doc-review';
import { BaseMessage } from '@/models/messages';
import documentReviewingService from '@/services/DocumentReviewingService';
import { useAuthStore } from '@/stores/authStore';
import { useReviewingStore } from '@/stores/reviewingStore';
import { useReferenceSelectionStore } from '@/stores/useReferenceSelectionStore';
import { OfficeUtils } from '@/utils/office.utils';
import { v4 as uuidv4 } from 'uuid';
import { h, onMounted, onUnmounted, ref } from 'vue';

const activeTabIndex = ref<string | number>(SubModulesEnum.DOC_REVIEWING_CHAT);
const chatRef = ref<ChatExposedMethods | null>(null);
const reviewSuggestionsStore = useReviewingStore();
const authStore = useAuthStore();
const referenceStore = useReferenceSelectionStore();

onMounted(() => {
  OfficeUtils.startDocumentSelectionListener(selectionChangedHandler);
});

onUnmounted(() => {
  OfficeUtils.stopDocumentSelectionListener(selectionChangedHandler);
});

const sendMessage = async (
  message: string,
  conversation: Conversation
): Promise<{ responseMessage: BaseMessage; pickle: string }> => {
  const paragraph = await OfficeUtils.getDocumentParagraphs();
  const response = await documentReviewingService.askQuestion(
    message,
    paragraph,
    conversation
  );

  return {
    responseMessage: response.responseMessage,
    pickle: response.pickle
  };
};

const suggestionsLoader = async () => {
  if (reviewSuggestionsStore.suggestions.length)
    return reviewSuggestionsStore.suggestions;

  const paragraphs = await OfficeUtils.getDocumentParagraphs();

  const suggestions =
    await documentReviewingService.getDocumentSuggestions(paragraphs);

  reviewSuggestionsStore.setSuggestions(suggestions);
  return suggestions;
};

const reviewingChatProps: ChatComponentProps = {
  assistantType: ReviewingAssistantType.DocumentReviewingAssistant,
  onSend: sendMessage,
  botMessageFooter: (message: BaseMessage) => {
    if ('references' in message) {
      return h(ReferenceDots, {
        referencedParagraphs: (message as ReviewMessage).references,
        onResetReview,
        renderId: uuidv4()
      });
    }
    return null;
  },
  suggestionsLoader
};

const onResetReview = (resetRefs: boolean = true) => {
  if (resetRefs) reviewSuggestionsStore.setSuggestions([]);
  chatRef.value?.resetConversation();
};

const selectionChangedHandler = async (): Promise<void> => {
  try {
    const text = await OfficeUtils.getSelectedText();
    if (
      !referenceStore.selectedReference?.selectedParagraphs.every((x) =>
        text.includes(x)
      )
    )
      referenceStore.resetSelection();
  } catch (error) {
    console.error('Error getting selected text:', error);
  }
};
</script>
<style lang="scss" scoped>
.screen-wrapper {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
  width: 100%;
}
</style>
